import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { api } from '../../services/api';
import { businessToday } from '../../utils/time';
import { groupWeek, dayHeading, WeekItem } from '../../utils/lawnCareWeek';

/**
 * Lawn care → This week. Read-only: every treatment round and add-on due in
 * the next 7 days, one list by day, with the mow it rides. Two small reads
 * (GET /treatments/this-week, GET /mow-addons/overview) laid out by
 * utils/lawnCareWeek. Nothing here changes anything; the links go to the job.
 */
const DAYS = 7;

export default function ThisWeekTab() {
  const today = businessToday();
  const { data: t, isLoading: a } = useQuery(['lawn-care-week', 'treatments', today], () => api.get('/treatments/this-week', { params: { days: DAYS, from: today } }).then(r => r.data));
  const { data: m, isLoading: b } = useQuery(['lawn-care-week', 'addons', today], () => api.get('/mow-addons/overview', { params: { days: DAYS, from: today } }).then(r => r.data));
  if (a || b) return <div className="py-16 flex justify-center"><Loader className="h-6 w-6 animate-spin text-brand-600" /></div>;
  const week = groupWeek(today, DAYS, { rounds: t?.rounds || [], legacy: t?.legacy || [], upcoming: m?.upcoming || [] });
  const total = week.reduce((n, d) => n + d.items.length, 0);
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600">{total ? <><b className="text-gray-900">{total}</b> due in the next {DAYS} days.</> : `Nothing due in the next ${DAYS} days.`}</p>
      {week.map(d => (
        <div key={d.d} className="card">
          <p className={`px-3 py-2 text-xs font-semibold border-b border-gray-100 ${d.d === today ? 'text-brand-700' : 'text-gray-700'}`}>{dayHeading(d.d, today)}{d.items.length ? ` · ${d.items.length}` : ''}</p>
          {d.items.length === 0
            ? <p className="px-3 py-2 text-xs text-gray-400">Nothing.</p>
            : <ul className="divide-y divide-gray-100">{d.items.map((it, i) => <Row key={`${it.kind}-${it.job_id}-${i}`} it={it} />)}</ul>}
        </div>
      ))}
    </div>
  );
}

function Row({ it }: { it: WeekItem }) {
  const tone = it.kind === 'addon' ? 'bg-sky-50 text-sky-800' : it.kind === 'legacy' ? 'bg-gray-100 text-gray-600' : 'bg-emerald-50 text-emerald-700';
  return (
    <li className="px-3 py-2 flex items-start gap-2">
      <span className={`mt-0.5 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${tone}`}>{it.kind === 'addon' ? 'add-on' : it.kind === 'legacy' ? 'old job' : 'round'}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 break-words">
          {it.customer_id ? <Link to={`/customers/${it.customer_id}`} className="font-semibold hover:underline">{it.customer_name}</Link> : <span className="font-semibold">{it.customer_name}</span>}
          <span className="text-gray-700"> · {it.label.replace(/^Add-on: |^Old job: /, '')}</span>
        </p>
        <p className="text-xs text-gray-500 break-words">
          {it.where}{it.who ? ` · ${it.who}` : ''} · {it.price_words}{it.by_rule ? ' · by rule' : ''}{it.billed ? ' · billed' : ''}
          {it.job_id ? <> · <Link to={`/jobs/${it.job_id}`} className="text-brand-600 hover:underline">job</Link></> : null}
        </p>
      </div>
    </li>
  );
}
